/**
 * The ONE list of languages Suikamon knows about, and which of them a player may pick.
 *
 * Adding a language is DATA, never gameplay code: drop its validated catalogs into
 * `shared/i18n/locales/<code>/`, then flip its `status` here to `"enabled"`. Nothing else in
 * the app names a locale. See `docs/i18n/README.md` for the import process.
 *
 * A locale is selectable only when it is `"enabled"`: validated and complete. `"planned"`
 * locales are known to the matcher (so a Spanish browser is not mistaken for anything else)
 * but resolve to the fallback until they ship.
 */

/** A BCP 47 tag, written exactly as the catalog folder is named. */
export type LocaleCode =
  | "en"
  | "es"
  | "pt-BR"
  | "zh-Hans"
  | "ja"
  | "de"
  | "fr"
  | "id"
  | "ko"
  | "ru";

/** The development-only pseudolocale (the conventional "English, accented" tag). */
export const PSEUDO_LOCALE = "en-XA" as const;
export type PseudoLocaleCode = typeof PSEUDO_LOCALE;

/** Any locale the app can render: a real one, or the dev pseudolocale. */
export type AppLocale = LocaleCode | PseudoLocaleCode;

/** English is the canonical source language and the last fallback. */
export const DEFAULT_LOCALE: LocaleCode = "en";

export type LocaleStatus = "enabled" | "planned";
export type LocalePriority = "default" | "high" | "medium" | "future";

export interface LocaleDefinition {
  readonly code: LocaleCode;
  /** How the language names itself — what the selector shows. Never a flag. */
  readonly nativeName: string;
  /** For docs, logs and the admin; never shown as the selector label. */
  readonly englishName: string;
  readonly priority: LocalePriority;
  readonly status: LocaleStatus;
  readonly direction: "ltr" | "rtl";
}

export const LOCALES: readonly LocaleDefinition[] = Object.freeze([
  { code: "en", nativeName: "English", englishName: "English", priority: "default", status: "enabled", direction: "ltr" },
  { code: "es", nativeName: "Español", englishName: "Spanish", priority: "high", status: "enabled", direction: "ltr" },
  { code: "pt-BR", nativeName: "Português (Brasil)", englishName: "Brazilian Portuguese", priority: "high", status: "enabled", direction: "ltr" },
  { code: "zh-Hans", nativeName: "简体中文", englishName: "Simplified Chinese", priority: "high", status: "enabled", direction: "ltr" },
  { code: "ja", nativeName: "日本語", englishName: "Japanese", priority: "high", status: "enabled", direction: "ltr" },
  { code: "de", nativeName: "Deutsch", englishName: "German", priority: "medium", status: "enabled", direction: "ltr" },
  { code: "fr", nativeName: "Français", englishName: "French", priority: "medium", status: "enabled", direction: "ltr" },
  { code: "id", nativeName: "Bahasa Indonesia", englishName: "Indonesian", priority: "medium", status: "enabled", direction: "ltr" },
  { code: "ko", nativeName: "한국어", englishName: "Korean", priority: "future", status: "enabled", direction: "ltr" },
  { code: "ru", nativeName: "Русский", englishName: "Russian", priority: "future", status: "enabled", direction: "ltr" },
] as const);

/**
 * Configurable cross-variant fallbacks, applied only after exact and script-compatible matching
 * have failed for a requested tag. The key is matched against the requested tag's
 * language-region (`pt-PT`) or bare language (`pt`).
 *
 * `pt-PT → pt-BR` is a product decision (REQ-01): European Portuguese readers are better served
 * by Brazilian Portuguese than by English. There is deliberately NO `zh-Hant → zh-Hans` entry:
 * Traditional Chinese readers are never silently handed Simplified.
 */
export const LOCALE_VARIANT_FALLBACKS: Readonly<Record<string, LocaleCode>> = Object.freeze({
  "pt-PT": "pt-BR",
  "pt-AO": "pt-BR",
  "pt-MZ": "pt-BR",
});

/**
 * The catalog namespaces. A key's first segment names its namespace
 * (`care.feed.button` lives in `care.json`), which the validator enforces.
 */
export const I18N_NAMESPACES = [
  "common",
  "navigation",
  "care",
  "battles",
  "evolution",
  "progression",
  "notifications",
  "errors",
  "account",
  "gamedata",
  "home",
  "story",
] as const;
export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

const LOCALE_BY_CODE: ReadonlyMap<string, LocaleDefinition> = new Map(
  LOCALES.map((l) => [l.code.toLowerCase(), l]),
);

/** The definition for a code (case-insensitive), or undefined if Suikamon does not know it. */
export function getLocaleDefinition(code: string): LocaleDefinition | undefined {
  return LOCALE_BY_CODE.get(code.toLowerCase());
}

/** True for a code in `LOCALES`, matched case-insensitively. */
export function isKnownLocale(code: string): code is LocaleCode {
  return LOCALE_BY_CODE.has(code.toLowerCase());
}

/** The canonical spelling of a known code (`pt-br` → `pt-BR`), or undefined. */
export function canonicalLocaleCode(code: string): LocaleCode | undefined {
  return LOCALE_BY_CODE.get(code.toLowerCase())?.code;
}

/** The locales a player may pick — validated and complete. Always includes the default. */
export function getEnabledLocales(): readonly LocaleDefinition[] {
  return LOCALES.filter((l) => l.status === "enabled");
}

export function getEnabledLocaleCodes(): readonly LocaleCode[] {
  return getEnabledLocales().map((l) => l.code);
}

export function isEnabledLocale(code: string): boolean {
  return getLocaleDefinition(code)?.status === "enabled";
}
